import { isDuplicateFailure, responseStatusOf } from "./requestFailure";

/**
 * 자동 구축 관련 409 가 FLAT 봉투(`{"status":"Failed","message":…,"code":…}`)에 싣는 코드 — 매핑 요청(`{"auto": true}`)과 자동
 * 인식 켜기가 받는다(mapping-request 「자동 구축 거절 코드의 알림 문구」). 뒤의 다섯은 사진 헤더 사전점검의 거절이다.
 */
export const AUTO_BUILD_REJECTION_CODES = [
  "AUTO_BUILD_UNAVAILABLE",
  "AUTO_DETECT_OFF",
  "GCP_FILE_MISSING",
  "GCP_FILE_UNREADABLE",
  "NO_PHOTOS",
  "AUTO_DETECT_LOCKED",
  "NO_RGB_PHOTOS",
  "MULTI_SENSOR",
  "DRONE_UNSUPPORTED",
  "NO_GNSS",
  "RTK_NOT_FIXED",
] as const;

export type AutoBuildRejectionCode =
  (typeof AUTO_BUILD_REJECTION_CODES)[number];

const HTTP_CONFLICT = 409;

const isRejectionCode = (value: unknown): value is AutoBuildRejectionCode =>
  typeof value === "string" &&
  (AUTO_BUILD_REJECTION_CODES as readonly string[]).includes(value);

type HttpFailure = { response?: { body?: unknown } };

/** 실패 본문의 `code` — 본문은 JSON 문자열이다(`RestApiError.response.body`). */
const bodyCodeOf = (error: unknown): unknown => {
  const body = (error as HttpFailure | null | undefined)?.response?.body;
  if (typeof body !== "string") return undefined;
  try {
    return (JSON.parse(body) as { code?: unknown } | null)?.code;
  } catch {
    return undefined;
  }
};

/**
 * 409 가 실은 자동 구축 거절 — 모르는 코드면 `"unknown"`, 409 가 아니거나 코드가 없으면(빈 문자열·문자열 아님 포함) `null` 이다.
 * 코드 없는 409 는 공용 가드(이미 요청됐거나 처리 중)다.
 */
function autoBuildRejectionOf(
  error: unknown
): AutoBuildRejectionCode | "unknown" | null {
  if (responseStatusOf(error) !== HTTP_CONFLICT) return null;
  const code = bodyCodeOf(error);
  if (isRejectionCode(code)) return code;
  return typeof code === "string" && code !== "" ? "unknown" : null;
}

/** 거절의 `projects` 문구 키 — 모르는 코드는 중립 안내다(mapping-request 「모르는 자동 구축 거절의 안내」). */
const rejectionMessageKey = (rejection: AutoBuildRejectionCode | "unknown") =>
  rejection === "unknown"
    ? ("timeSeries.autoBuildRejectedUnknown" as const)
    : (`timeSeries.autoBuildRejected.${rejection}` as const);

/** 매핑 요청 실패 알림의 `projects` 문구 키(mapping-request 「매핑 요청 결과 알림」). */
export function mappingRequestFailureMessageKey(error: unknown) {
  const rejection = autoBuildRejectionOf(error);
  if (rejection) return rejectionMessageKey(rejection);
  if (isDuplicateFailure(error))
    return "timeSeries.mappingRequestResult.alreadyRequested" as const;
  return "timeSeries.mappingRequestResult.failed" as const;
}

/** 자동 인식 토글 저장 실패 알림의 `projects` 문구 키(mapping-request 「자동 인식 토글 저장」). */
export function gcpAutoDetectSaveFailureMessageKey(error: unknown) {
  const rejection = autoBuildRejectionOf(error);
  return rejection
    ? rejectionMessageKey(rejection)
    : ("timeSeries.gcpAutoDetectSaveFailed" as const);
}
